// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 ClubView

import { useViewerRole } from '../context/SessionContext';

/**
 * Subset of the Member shape that MemberName needs. All name/email fields are
 * optional because the API-generated `Member` type marks them optional and we
 * want this helper to drop in everywhere without per-call type narrowing.
 */
export type DisplayableMember = {
  first_name?: string | null;
  last_name?: string | null;
  is_child?: boolean;
  email?: string | null;
};

/**
 * Roles that always see full names (coaches/admins/instructors need it for
 * registration ledgers, attendance, and safeguarding context). Every other
 * role sees children as "First L." per W66 / ADR-001.
 */
const FULL_NAME_ROLES = new Set(['admin', 'coach', 'instructor']);

/**
 * formatMemberDisplayName returns the name string for a member as seen by a
 * given viewer role. Children rendered to non-coach roles get first name +
 * last initial only (e.g. "Sarah M.").
 *
 * W69 — implements the display rule in W66 / ADR-001. Always go through this
 * helper or the <MemberName> component below; raw `${first} ${last}` is
 * forbidden in production code (lint guard in scripts/check-member-names.sh).
 */
// This helper is deliberately colocated with the component: scripts/check-member-names.sh
// names this file as the single place raw member-name access is allowed, and every render
// site must come through the component or this helper. Splitting it out would mean two
// exempted files instead of one, weakening that guard to satisfy a fast-refresh hint.
// eslint-disable-next-line react-refresh/only-export-components
export function formatMemberDisplayName(
  member: DisplayableMember | null | undefined,
  viewerRole: string,
  opts: { showEmail?: boolean } = {},
): string {
  if (!member) return '';

  const first = (member.first_name ?? '').trim();
  const last = (member.last_name ?? '').trim();
  const isFullViewer = FULL_NAME_ROLES.has(viewerRole);
  const maskChild = member.is_child === true && !isFullViewer;

  let name: string;
  if (!last) {
    name = first;
  } else if (maskChild) {
    // Use Array.from so multi-code-unit graphemes (rare CJK, emoji) yield a
    // sensible single-character initial. NFC-composed accents pass through
    // unchanged (Ó. not O´).
    const initial = Array.from(last)[0] ?? '';
    name = first ? `${first} ${initial}.` : `${initial}.`;
  } else {
    name = first ? `${first} ${last}` : last;
  }

  if (opts.showEmail && member.email && !maskChild) {
    return `${name} (${member.email})`;
  }
  return name;
}

/**
 * <MemberName> renders a member's name through `formatMemberDisplayName`,
 * pulling the current viewer role from the session context.
 *
 * Pass `viewerRole` to override the session role (for tests or for places
 * where the role differs from the auth session — rare).
 */
export type MemberNameProps = {
  member: DisplayableMember | null | undefined;
  viewerRole?: string;
  showEmail?: boolean;
};

export function MemberName({ member, viewerRole, showEmail }: MemberNameProps) {
  const sessionRole = useViewerRole();
  const effectiveRole = viewerRole ?? sessionRole;
  return <>{formatMemberDisplayName(member, effectiveRole, { showEmail })}</>;
}
